import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { CartProvider } from "@/context/CartContext";
import { ModalProvider, useModals } from "@/context/ModalContext";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { CartDrawer } from "@/components/CartDrawer";
import { ConsultationModal } from "@/components/ConsultationModal";
import { WhiteGloveModal } from "@/components/WhiteGloveModal";
import { QuoteModal } from "@/components/QuoteModal";
import { ChatWidget } from "@/components/ChatWidget";
import { Headphones } from "lucide-react";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Quiz from "@/pages/Quiz";
import Commercial from "@/pages/Commercial";
import About from "@/pages/About";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import { GuidesList, GuideDetail } from "@/pages/Guides";
import BrandPage from "@/pages/BrandPage";
import BrandsIndex from "@/pages/BrandsIndex";
import CollectionPage from "@/pages/CollectionPage";
import PolicyPage from "@/pages/PolicyPage";
import Confirmation from "@/pages/Confirmation";
import { RouteSeo } from "@/components/Seo";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => window.scrollTo(0, 0), [pathname]);
  return null;
};

const ExpertFab = () => {
  const { openConsultation } = useModals();
  return (
    <button data-testid="expert-fab" onClick={() => openConsultation()}
      className="glow-fab fixed bottom-6 left-6 z-40 flex items-center gap-2 bg-gradient-to-r from-amber to-gold text-espresso pl-4 pr-5 py-3.5 font-semibold rounded-full hover:scale-[1.04] active:scale-100 transition-transform duration-300">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-forest opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-forest" />
      </span>
      <Headphones size={18} />
      <span className="hidden sm:inline text-sm">Talk to an Expert</span>
    </button>
  );
};

function App() {
  return (
    <div className="App bg-espresso min-h-screen">
      <BrowserRouter>
        <CartProvider>
          <ModalProvider>
            <ScrollToTop />
            <RouteSeo />
            <Header />
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/shop/c/:handle" element={<CollectionPage />} />
                <Route path="/shop/:category" element={<Shop />} />
                <Route path="/product/:handle" element={<ProductDetail />} />
                <Route path="/quiz" element={<Quiz />} />
                <Route path="/commercial" element={<Commercial />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/guides" element={<GuidesList />} />
                <Route path="/guides/:slug" element={<GuideDetail />} />
                <Route path="/brands" element={<BrandsIndex />} />
                <Route path="/brands/:brand" element={<BrandPage />} />
                <Route path="/policies/:handle" element={<PolicyPage />} />
                <Route path="/checkout/confirmation" element={<Confirmation />} />
              </Routes>
            </main>
            <Footer />
            <CartDrawer />
            <ConsultationModal />
            <WhiteGloveModal />
            <QuoteModal />
            <ExpertFab />
            <ChatWidget />
            <Toaster position="bottom-center" theme="dark" richColors />
          </ModalProvider>
        </CartProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
